import React from "react";
import { Link } from "react-router-dom";

const PageHero = ({
  eyebrow,
  title,
  description,
  image,
  imageAlt,
  primaryAction,
  secondaryAction,
  stats,
  compact = false,
  showImageCaption = true,
}) => {
  return (
    <section className="relative mt-16 overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(190,242,100,0.22),_transparent_30%),radial-gradient(circle_at_top_right,_rgba(185,28,28,0.16),_transparent_25%),linear-gradient(180deg,#0f2417_0%,#17311f_55%,#f7fbef_100%)]">
      <div className="absolute inset-0 bg-[linear-gradient(120deg,rgba(15,23,42,0.44),rgba(15,23,42,0.14))]" />
      <div
        className={`relative mx-auto grid max-w-7xl items-center gap-10 px-6 py-16 lg:px-8 ${
          compact ? "min-h-[420px] lg:grid-cols-[1.05fr_0.95fr]" : "min-h-[720px] lg:grid-cols-2"
        }`}
      >
        <div className="max-w-2xl text-white">
          {eyebrow ? (
            <div className="inline-flex rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-lime-100 backdrop-blur">
              {eyebrow}
            </div>
          ) : null}
          <h1 className={`mt-6 font-black tracking-tight text-white ${compact ? "text-4xl sm:text-5xl" : "text-5xl sm:text-6xl lg:text-7xl"}`}>
            {title}
          </h1>
          <p className={`mt-6 max-w-xl leading-8 text-slate-100 ${compact ? "text-base sm:text-lg" : "text-base sm:text-lg"}`}>
            {description}
          </p>
          {primaryAction || secondaryAction ? (
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              {primaryAction ? <ActionButton action={primaryAction} primary /> : null}
              {secondaryAction ? <ActionButton action={secondaryAction} /> : null}
            </div>
          ) : null}
          {stats?.length ? (
            <div className="mt-8 grid max-w-xl grid-cols-2 gap-3 sm:grid-cols-4">
              {stats.map((item) => (
                <div key={item.label} className="rounded-2xl border border-white/15 bg-white/10 px-4 py-3 backdrop-blur">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-lime-100/80">{item.label}</p>
                  <p className="mt-1 text-sm font-bold text-white">{item.value}</p>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        <div className="relative">
          <div className="absolute -left-6 top-10 h-28 w-28 rounded-full bg-lime-300/20 blur-3xl" />
          <div className="absolute -right-4 bottom-8 h-36 w-36 rounded-full bg-red-500/20 blur-3xl" />
          <div className="relative overflow-hidden rounded-[2rem] border border-white/20 bg-white/10 p-3 shadow-[0_30px_80px_rgba(15,23,42,0.35)] backdrop-blur-xl">
            <img
              src={image}
              alt={imageAlt}
              className={`w-full rounded-[1.5rem] object-cover ${compact ? "h-[300px]" : "h-[520px]"}`}
              width="1200"
              height={compact ? "800" : "1600"}
              loading="eager"
              fetchPriority="high"
              decoding="async"
            />
            {showImageCaption ? <div className="absolute inset-x-6 bottom-6 rounded-[1.25rem] border border-white/15 bg-slate-950/55 p-4 text-white backdrop-blur">
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-lime-200">
                Zea Mays Foods
              </p>
              <p className="mt-1 text-lg font-bold">
                {compact ? "A cleaner, more polished experience." : "A smoother way to browse, order, and track."}
              </p>
              <p className="mt-1 text-sm text-slate-200">
                {compact ? "Consistent design, stronger hierarchy, and better readability." : "Built for live products, fast checkout, and a professional customer flow."}
              </p>
            </div> : null}
          </div>
        </div>
      </div>
    </section>
  );
};

const ActionButton = ({ action, primary = false }) => {
  if (!action) return null;

  const className = primary
    ? "inline-flex items-center justify-center rounded-full bg-red-700 px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-red-950/20 transition hover:bg-red-800"
    : "inline-flex items-center justify-center rounded-full border border-white/20 bg-white/10 px-6 py-3.5 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/15";

  return action.href ? (
    <a href={action.href} className={className}>
      {action.label}
    </a>
  ) : (
    <Link to={action.to} state={action.state} className={className}>
      {action.label}
    </Link>
  );
};

export default PageHero;
